import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type {
  SuggestedActionResponse,
  ExecuteActionResult,
  EmailDraftSummary,
} from '@erp/rpc';

/** Polling interval (ms) while an agent is running for this entity. */
const AGENT_RUNNING_POLL_INTERVAL = 3_000;

/**
 * Fetch pending suggested actions and email draft for an entity.
 * Returns actions from the most recent agent run, plus the latest
 * AI-generated email draft for the conversation thread (if any).
 *
 * When a validation agent is currently running for the entity the query
 * automatically polls every 3 seconds so the UI updates as soon as results
 * are available.
 */
export function useSuggestedActions(
  entityType: string | undefined,
  entityId: string | undefined,
  conversationId?: string | null,
  options?: { enabled?: boolean },
) {
  const query = useQuery({
    ...orpc.suggestedActions.listByEntity.queryOptions({
      input:
        entityType && entityId
          ? {
              entityType,
              entityId,
              ...(conversationId ? { conversationId } : {}),
            }
          : skipToken,
    }),
    enabled:
      Boolean(entityType) && Boolean(entityId) && (options?.enabled ?? true),
    refetchInterval: (q) => {
      // Poll while the agent is still running; stop once it finishes.
      if (q.state.data?.isAgentRunning) {
        return AGENT_RUNNING_POLL_INTERVAL;
      }
      return false;
    },
  });

  return {
    ...query,
    actions: query.data?.actions ?? [],
    emailDraft: query.data?.emailDraft ?? null,
    isAgentRunning: query.data?.isAgentRunning ?? false,
  };
}

/**
 * Approve and execute a suggested action.
 * Invalidates the action list on success so the UI updates.
 */
export function useApproveAction() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    ...orpc.suggestedActions.approveAndExecute.mutationOptions(),
    onSuccess: (data: ExecuteActionResult) => {
      showToast({
        title: data.success ? 'Action applied' : 'Action failed',
        message: data.message,
        color: data.success ? 'success' : 'error',
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.suggestedActions.listByEntity.key(),
      });
    },
    onError: (error: Error) => {
      showToast({
        title: 'Failed to apply action',
        message: error.message,
        color: 'error',
      });
    },
  });
}

/**
 * Reject a suggested action.
 * Invalidates the action list on success so the UI updates.
 */
export function useRejectAction() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    ...orpc.suggestedActions.reject.mutationOptions(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.suggestedActions.listByEntity.key(),
      });
    },
    onError: (error: Error) => {
      showToast({
        title: 'Failed to reject action',
        message: error.message,
        color: 'error',
      });
    },
  });
}

export type { SuggestedActionResponse, ExecuteActionResult, EmailDraftSummary };
